import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import SimpleAlbumCard from './SimpleAlbumCard';

const getItemsPerView = (width) => {
  if (width >= 1280) return 3;
  if (width >= 768) return 2;
  return 1;
};

const SimpleAlbumsSection = ({ albums, onViewAlbum }) => {
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const itemsPerView = getItemsPerView(viewportWidth);
  const maxIndex = Math.max(0, albums.length - itemsPerView);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const trackStyle = useMemo(
    () => ({
      width: `${(albums.length / itemsPerView) * 100}%`,
      transform: `translateX(-${(currentIndex * 100) / albums.length}%)`
    }),
    [albums.length, currentIndex, itemsPerView]
  );

  const goPrev = () => setCurrentIndex((prev) => Math.max(0, prev - 1));
  const goNext = () => setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));

  return (
    <section id="albums" className="perf-section bg-white py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 space-y-4 text-center">
          <span className="text-sm font-medium uppercase tracking-widest text-gray-500">Our Work</span>
          <h2 className="text-5xl font-bold text-gray-900 md:text-6xl">Wedding Albums</h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Each album tells a unique love story, captured with passion and artistry
          </p>
        </div>

        <div className="relative">
          <Button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute left-2 top-1/2 z-10 h-12 w-12 -translate-y-1/2 rounded-full bg-gray-900 p-0 text-white shadow-2xl hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40 md:-left-6 md:h-14 md:w-14"
            variant="default"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <Button
            onClick={goNext}
            disabled={currentIndex >= maxIndex}
            className="absolute right-2 top-1/2 z-10 h-12 w-12 -translate-y-1/2 rounded-full bg-gray-900 p-0 text-white shadow-2xl hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40 md:-right-6 md:h-14 md:w-14"
            variant="default"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>

          <div className="overflow-hidden">
            <div className="transition-transform duration-500 ease-out" style={trackStyle}>
              <div className="flex">
                {albums.map((album, index) => (
                  <div
                    key={album.id}
                    className="shrink-0 px-3"
                    style={{ width: `${100 / albums.length}%` }}
                  >
                    <SimpleAlbumCard album={album} index={index} onViewAlbum={onViewAlbum} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {albums.length > itemsPerView && (
            <div className="mt-8 flex justify-center gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={`album-dot-${index}`}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    currentIndex === index ? 'w-8 bg-gray-900' : 'w-2.5 bg-gray-300'
                  }`}
                  aria-label={`Go to album slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SimpleAlbumsSection;
